import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrdersByPhoneNumber } from './services/firebaseService';
import Header from './Header';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VisibilityIcon from '@mui/icons-material/Visibility';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  rejected: 'bg-red-100 text-red-800'
};

const statusIcons = {
  processing: <AssignmentIcon className="!w-5 !h-5" />,
  confirmed: <CheckCircleIcon className="!w-5 !h-5" />,
  shipped: <LocalShippingIcon className="!w-5 !h-5" />,
  delivered: <CheckCircleIcon className="!w-5 !h-5" />,
  cancelled: <CancelIcon className="!w-5 !h-5" />,
  rejected: <CancelIcon className="!w-5 !h-5" />
};

const CheckOrderStatus = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Handle Firebase Timestamp
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }
    
    return dateObj.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      setError(t('checkOrderStatus.pleaseEnterPhone'));
      return;
    }

    setLoading(true);
    setError('');
    setSearched(false);
    setOrders([]);

    try {
      const result = await getOrdersByPhoneNumber(phoneNumber.trim());
      if (result.success) {
        setOrders(result.orders);
        setSearched(true);
        if (result.orders.length === 0) {
          setError(t('checkOrderStatus.noOrdersFound'));
        }
      } else {
        setError(t('checkOrderStatus.searchFailed') + ': ' + result.error);
      }
    } catch (err) {
      console.error('Error searching orders:', err);
      setError(t('checkOrderStatus.tryAgain'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (orderId) => {
    navigate(`/customer-order/${orderId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-grow pt-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-primary text-white px-6 py-4">
              <h1 className="text-2xl font-bold flex items-center">
                <SearchIcon className="mr-3 !w-6 !h-6" />
                {t('checkOrderStatus.title')}
              </h1>
              <p className="text-sm mt-2 text-blue-100">{t('checkOrderStatus.subtitle')}</p>
            </div>

            <form onSubmit={handleSearch} className="p-6">
              <div className="mb-4">
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  <PhoneIcon className="inline !w-4 !h-4 mr-1" />
                  {t('checkOrderStatus.phoneNumber')}
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('checkOrderStatus.phonePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 text-lg"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">{t('checkOrderStatus.phoneHelper')}</p>
              </div>

              {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-primary hover:bg-blue-900 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    {t('checkOrderStatus.searching')}
                  </>
                ) : (
                  <>
                    <SearchIcon className="mr-2 !w-5 !h-5" />
                    {t('checkOrderStatus.searchOrders')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Orders List */}
          {searched && orders.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-xl font-bold text-primary">{t('checkOrderStatus.yourOrders')}</h2>
                <p className="text-sm text-gray-600 mt-1">{orders.length} {t('checkOrderStatus.ordersFound')}</p>
              </div>
              <div className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <div key={order.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <AssignmentIcon className="!w-5 !h-5 text-primary" />
                          <h3 className="font-bold text-lg text-gray-900">
                            {t('checkOrderStatus.orderPrefix')}{order.orderId || order.id?.slice(-8).toUpperCase()}
                          </h3>
                        </div>
                        <div className="ml-8 space-y-1 text-sm text-gray-600">
                          <p><strong>{t('checkOrderStatus.date')}:</strong> {formatDate(order.dateTime)}</p>
                          <p><strong>{t('checkOrderStatus.totalAmount')}:</strong> <span className="text-primary font-semibold">{formatCurrency(order.totalAmount || order.total)}</span></p>
                          <p><strong>{t('checkOrderStatus.items')}:</strong> {order.products?.length || 0} {t('checkOrderStatus.itemsCount')}</p>
                        </div>
                      </div>
                      <div className="flex flex-col md:items-end gap-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                          {statusIcons[order.status] || <AssignmentIcon className="!w-4 !h-4 mr-1" />}
                          <span className="ml-1">{order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}</span>
                        </span>
                        <button
                          onClick={() => handleViewDetails(order.id)}
                          className="flex items-center gap-2 text-primary hover:text-blue-900 font-medium text-sm transition-colors"
                        >
                          <VisibilityIcon className="!w-4 !h-4" />
                          {t('checkOrderStatus.viewDetails')}
                        </button>
                      </div>
                    </div>

                    {/* Additional Information for Shipped Orders */}
                    {(order.courier || order.trackingNumber || order.invoiceNumber) && (
                      <div className="ml-8 mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          {order.courier && (
                            <div>
                              <p className="text-gray-600">{t('checkOrderStatus.courier')}:</p>
                              <p className="font-medium">{order.courier}</p>
                            </div>
                          )}
                          {order.trackingNumber && (
                            <div>
                              <p className="text-gray-600">{t('checkOrderStatus.tracking')}:</p>
                              <p className="font-mono font-medium text-blue-600">{order.trackingNumber}</p>
                            </div>
                          )}
                          {order.invoiceNumber && (
                            <div>
                              <p className="text-gray-600">{t('checkOrderStatus.invoice')}:</p>
                              <p className="font-mono font-medium text-green-600">{order.invoiceNumber}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {searched && orders.length === 0 && !error && (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <p className="text-gray-500 text-lg">{t('checkOrderStatus.noOrdersMessage')}</p>
              <p className="text-gray-400 text-sm mt-2">{t('checkOrderStatus.checkPhoneNumber')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckOrderStatus;

